import * as React from 'react';
import { Box, Button, Paper, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { TaskflowWizardStep } from './taskflowBuilderContext';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        stepper: {
            display: 'flex',
            alignItems: 'center',
            overflowX: 'auto',
            padding: theme.spacing(0, 0, 1, 0),
        },
        stepButton: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: theme.spacing(1, 0.5),
            flexShrink: 0,
        },
        stepNumber: {
            width: 26,
            height: 26,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
            border: `2px solid ${theme.palette.grey[300]}`,
            color: theme.palette.text.disabled,
            flexShrink: 0,
        },
        stepNumberActive: {
            backgroundColor: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
        },
        stepNumberDone: {
            backgroundColor: theme.palette.success.main,
            borderColor: theme.palette.success.main,
            color: theme.palette.success.contrastText,
        },
        stepLabel: {
            fontSize: 12.5,
            fontWeight: 700,
            color: theme.palette.text.disabled,
        },
        stepLabelActive: {
            color: theme.palette.text.primary,
        },
        stepRule: {
            width: 28,
            height: 2,
            backgroundColor: theme.palette.grey[300],
            flexShrink: 0,
            margin: theme.spacing(0, 0.25),
        },
        frame: {
            borderRadius: 12,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
        },
        frameBody: {
            flex: 1,
            minHeight: 480,
            display: 'flex',
            flexDirection: 'column',
        },
        frameFoot: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: theme.spacing(1.5, 2.5),
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.grey[50],
            flexShrink: 0,
        },
        caption: {
            fontSize: 12,
            fontWeight: 700,
            color: theme.palette.text.disabled,
        },
    })
);

interface WizardStepDefinition {
    step: TaskflowWizardStep;
    label: string;
}

const WIZARD_STEPS: WizardStepDefinition[] = [
    { step: 'library', label: 'Library' },
    { step: 'nameScope', label: 'Name & scope' },
    { step: 'taskList', label: 'Task list' },
    { step: 'publish', label: 'Save & publish' },
];

interface WizardFrameProps {
    currentStep: TaskflowWizardStep;
    onStepClick: (step: TaskflowWizardStep) => void;
    onBack: () => void;
    onNext: () => void;
    backDisabled: boolean;
    nextDisabled?: boolean;
    nextLabel: string;
    footerCaption: string;
    children: React.ReactNode;
}

export const WizardFrame: React.FC<WizardFrameProps> = (props) => {
    const classes = useStyles();
    const currentIndex = WIZARD_STEPS.findIndex(definition => definition.step === props.currentStep);

    return (
        <>
            <Box className={classes.stepper}>
                {WIZARD_STEPS.map((definition, index) => {
                    const isActive = index === currentIndex;
                    const isDone = index < currentIndex;
                    return (
                        <React.Fragment key={definition.step}>
                            <button
                                type="button"
                                className={classes.stepButton}
                                onClick={() => props.onStepClick(definition.step)}
                            >
                                <span className={`${classes.stepNumber} ${isActive ? classes.stepNumberActive : ''} ${isDone ? classes.stepNumberDone : ''}`}>
                                    {isDone ? <CheckIcon style={{ fontSize: 15 }} /> : index + 1}
                                </span>
                                <span className={`${classes.stepLabel} ${isActive ? classes.stepLabelActive : ''}`}>
                                    {definition.label}
                                </span>
                            </button>
                            {index < WIZARD_STEPS.length - 1 && <span className={classes.stepRule} />}
                        </React.Fragment>
                    );
                })}
            </Box>

            <Paper variant="outlined" className={classes.frame}>
                <Box className={classes.frameBody}>
                    {props.children}
                </Box>
                <Box className={classes.frameFoot}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ArrowBackIcon />}
                        disabled={props.backDisabled}
                        onClick={props.onBack}
                    >
                        Back
                    </Button>
                    <Typography className={classes.caption}>{props.footerCaption}</Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        endIcon={<ArrowForwardIcon />}
                        disabled={props.nextDisabled}
                        onClick={props.onNext}
                    >
                        {props.nextLabel}
                    </Button>
                </Box>
            </Paper>
        </>
    );
};
